const keyReplacements = {
  ge: 'ka',
  jp: 'ja',
  kr: 'ko',
  ml: 'ms'
}

const keysToProcess = new Set([
  'default_language',
  'enabled_languages',
  'reviewed_languages',
  'available_languages'
])

export function processLanguageValue (value) {
  if (typeof value === 'string') {
    return keyReplacements[value] || value
  }
  if (Array.isArray(value)) {
    return value.map(v => typeof v === 'string' ? keyReplacements[v] || v : v)
  }
  return value
}

export function replaceWrongLanguageCodes (data) {
  if (Array.isArray(data)) {
    return data.map(item => replaceWrongLanguageCodes(item))
  } else if (data !== null && typeof data === 'object') {
    const result = {}
    for (const key in data) {
      const newKey = keyReplacements[key] || key
      if (keysToProcess.has(key)) {
        result[newKey] = processLanguageValue(data[key])
      } else {
        result[newKey] = replaceWrongLanguageCodes(data[key])
      }
    }
    return result
  }
  return data
}

export function removeHtmlTags (input, parser = new DOMParser()) {
  if (typeof input === 'string') {
    try {
      const doc = parser.parseFromString(input, 'text/html')
      return doc.body.textContent || ''
    } catch (error) {
      console.error('Error parsing HTML:', error)
      return input
    }
  }
  if (Array.isArray(input)) {
    return input.map(item => removeHtmlTags(item, parser))
  }
  if (input !== null && typeof input === 'object') {
    const result = {}
    for (const key in input) {
      if (Object.hasOwnProperty.call(input, key)) {
        result[key] = removeHtmlTags(input[key], parser)
      }
    }
    return result
  }
  return input
}

export function removeHtmlTagsFromDividers (obj) {
  if (Array.isArray(obj)) {
    return obj.map(removeHtmlTagsFromDividers)
  } else if (obj && typeof obj === 'object') {
    if (obj.type === 'divider' && obj.data && obj.data.content) {
      obj.data.content = removeHtmlTags(obj.data.content)
    }
    for (const key in obj) {
      if (Object.hasOwnProperty.call(obj, key)) {
        obj[key] = removeHtmlTagsFromDividers(obj[key])
      }
    }
  }
  return obj
}

export function migrateTranslations (config) {
  const editor = config.config_json.editor
  const published = config.config_json.published

  if (!editor.enabled_languages) {
    editor.enabled_languages = []
    editor.reviewed_languages = []
    if (editor.default_language) {
      editor.enabled_languages.push(editor.default_language)
      editor.reviewed_languages.push(editor.default_language)
    }
  }

  if (Object.keys(published).length) {
    if (!published.enabled_languages) {
      published.enabled_languages = []
      published.reviewed_languages = []
      if (editor.default_language) {
        published.enabled_languages.push(editor.default_language)
        published.reviewed_languages.push(editor.default_language)
      }
    }
  }

  // Ensures backward compatibility by migrating the legacy array 'available_languages'
  if (editor.available_languages.length > 1) {
    editor.enabled_languages = [...new Set(editor.available_languages)]
    editor.reviewed_languages = [...new Set(editor.available_languages)]
    editor.available_languages = ['en']
  }

  if (published.available_languages?.length > 1) {
    published.enabled_languages = [...new Set(published.available_languages)]
    published.reviewed_languages = [...new Set(published.available_languages)]
    published.available_languages = ['en']
  }

  return config
}

export function convertToMention (string, findDynamicVariable) {
  const matches = string.match(/\{(.*?)\}/)

  if (matches) {
    const submatch = matches[1]
    const before = string.substr(0, string.indexOf('{'))
    const after = string.substr(string.indexOf('}') + 1)
    return `${before}<span data-mention-id="${findDynamicVariable(submatch).key}">@${submatch}</span>${after}`
  }
  return string
}

export function convertJourneyLocationsToObjects (config, findDynamicVariable) {
  const editorConfig = config.config_json.editor
  const publishedConfig = config.config_json.published
  const defaultLang = config.config_json.editor.default_language

  if (editorConfig['track-and-trace']?.journey.length) {
    editorConfig['track-and-trace'].journey.forEach(step => {
      if (typeof step.location !== 'object') {
        const locationStr = step.location
        step.location = {}
        step.location[defaultLang] = convertToMention(locationStr, findDynamicVariable)
      }
    })
  }

  if (publishedConfig && !Array.isArray(publishedConfig) && publishedConfig['track-and-trace']?.journey.length) {
    publishedConfig['track-and-trace'].journey.forEach(step => {
      if (typeof step.location !== 'object') {
        const locationStr = step.location
        step.location = {}
        step.location[defaultLang] = convertToMention(locationStr, findDynamicVariable)
      }
    })
  }

  return config
}

export function convertToSTCString (json, singleLine) {
  let string = ''

  json.forEach((block) => {
    if (block.type === 'paragraph' && !singleLine) {
      string += '<p>'
    }
    if (block.content) {
      block.content.forEach((elem) => {
        if (elem.type === 'text') {
          if (elem.marks) {
            elem.marks.forEach((mark) => {
              if (mark.type === 'bold') {
                string += '<b>'
              } else if (mark.type === 'italic') {
                string += '<i>'
              } else if (mark.type === 'underline') {
                string += '<u>'
              } else if (mark.type === 'link') {
                string += `<a href="${mark.attrs.href}">`
              }
            })
          }

          string += elem.text

          if (elem.marks) {
            elem.marks.forEach((mark) => {
              if (mark.type === 'bold') {
                string += '</b>'
              } else if (mark.type === 'italic') {
                string += '</i>'
              } else if (mark.type === 'underline') {
                string += '</u>'
              } else if (mark.type === 'link') {
                string += '</a>'
              }
            })
          }
        } else if (elem.type === 'mention') {
          string += `{${elem.attrs.id}}`
        } else if (elem.type === 'hashtag') {
          string += `{#${elem.attrs.id}}`
        }
      })
    }
    if (block.type === 'paragraph' && !singleLine) {
      string += '</p>'
    }
  })

  return string
}
